import { useState } from "react";
import {
  useGetIdentity,
  useGetOne,
  useNotify,
  useTranslate,
  useUpdate,
} from "ra-core";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

import { ListCardsSkeleton, ListErrorState } from "../design/ListStates";
import { HELP_CLASS, SectionCard } from "../guardias/guardiasShared";
import type { WhatsAppConfig } from "../types";
import {
  configToForm,
  parseConfigForm,
  type ConfigField,
  type ConfigFormValues,
} from "./whatsappSettingsRules";

const NUMBER_FIELDS: { field: ConfigField; key: string }[] = [
  { field: "windowDirectHours", key: "window_direct" },
  { field: "ctwaFreeHours", key: "ctwa_free" },
  { field: "monthlyFreeQuota", key: "quota" },
  { field: "alert24hHoursLeft", key: "alert_24h" },
  { field: "alertCtwaDaysLeft", key: "alert_ctwa" },
  { field: "sendRateLimit", key: "rate_limit" },
];

const FlagRow = ({
  id,
  label,
  help,
  checked,
  onChange,
}: {
  id: string;
  label: string;
  help: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) => {
  const translate = useTranslate();
  return (
    <div className="flex items-start justify-between gap-4 rounded-md border p-3">
      <div className="space-y-1">
        <Label htmlFor={id} className="text-base font-semibold">
          {label}
        </Label>
        <p className={HELP_CLASS}>{help}</p>
        <p className="text-[15px] font-medium" data-testid={`${id}-state`}>
          {translate(
            checked
              ? "crm.whatsapp.settings.flag_on"
              : "crm.whatsapp.settings.flag_off",
          )}
        </p>
      </div>
      <Switch id={id} checked={checked} onCheckedChange={onChange} />
    </div>
  );
};

const ConfigForm = ({ config }: { config: WhatsAppConfig }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const [update, { isPending }] = useUpdate();
  const [values, setValues] = useState<ConfigFormValues>(() =>
    configToForm(config),
  );
  const [invalid, setInvalid] = useState<ConfigField[]>([]);
  const set = <K extends keyof ConfigFormValues>(
    key: K,
    value: ConfigFormValues[K],
  ) => setValues((current) => ({ ...current, [key]: value }));

  const onSave = () => {
    const result = parseConfigForm(values);
    if (!result.ok) {
      setInvalid(result.fields);
      return;
    }
    setInvalid([]);
    update(
      "whatsapp_config",
      {
        id: config.id,
        data: { ...result.patch, updated_by: identity?.id ?? null },
        previousData: config,
      },
      {
        onSuccess: () =>
          notify("crm.whatsapp.settings.saved", { type: "success" }),
        onError: () =>
          notify("crm.whatsapp.settings.save_error", { type: "error" }),
      },
    );
  };

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <h3 className="text-base font-semibold">
          {translate("crm.whatsapp.settings.go_live_title")}
        </h3>
        <FlagRow
          id="whatsapp-templates-enabled"
          label={translate("crm.whatsapp.settings.templates_enabled")}
          help={translate("crm.whatsapp.settings.templates_enabled_help")}
          checked={values.templatesEnabled}
          onChange={(checked) => set("templatesEnabled", checked)}
        />
        <FlagRow
          id="whatsapp-welcome-enabled"
          label={translate("crm.whatsapp.settings.welcome_enabled")}
          help={translate("crm.whatsapp.settings.welcome_enabled_help")}
          checked={values.welcomeEnabled}
          onChange={(checked) => set("welcomeEnabled", checked)}
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {NUMBER_FIELDS.map(({ field, key }) => (
          <div key={field} className="space-y-1">
            <Label htmlFor={`whatsapp-${key}`}>
              {translate(`crm.whatsapp.settings.${key}`)}
            </Label>
            <Input
              id={`whatsapp-${key}`}
              inputMode="numeric"
              className="h-11 text-base tabular-nums"
              value={values[field] as string}
              aria-invalid={invalid.includes(field)}
              onChange={(event) => set(field, event.target.value as never)}
            />
            <p className={HELP_CLASS}>
              {translate(`crm.whatsapp.settings.${key}_help`)}
            </p>
          </div>
        ))}
      </div>

      <div className="space-y-3">
        <h3 className="text-base font-semibold">
          {translate("crm.whatsapp.settings.welcome_title")}
        </h3>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="whatsapp-welcome-name">
              {translate("crm.whatsapp.settings.welcome_name")}
            </Label>
            <Input
              id="whatsapp-welcome-name"
              className="h-11 text-base"
              value={values.welcomeName}
              aria-invalid={invalid.includes("welcomeName")}
              onChange={(event) => set("welcomeName", event.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="whatsapp-welcome-language">
              {translate("crm.whatsapp.settings.welcome_language")}
            </Label>
            <Input
              id="whatsapp-welcome-language"
              className="h-11 text-base"
              value={values.welcomeLanguage}
              aria-invalid={invalid.includes("welcomeLanguage")}
              onChange={(event) => set("welcomeLanguage", event.target.value)}
            />
          </div>
        </div>
        <div className="space-y-1">
          <Label htmlFor="whatsapp-welcome-variables">
            {translate("crm.whatsapp.settings.welcome_variables")}
          </Label>
          <Input
            id="whatsapp-welcome-variables"
            className="h-11 text-base"
            value={values.welcomeVariables}
            onChange={(event) => set("welcomeVariables", event.target.value)}
          />
          <p className={HELP_CLASS}>
            {translate("crm.whatsapp.settings.welcome_variables_help")}
          </p>
        </div>
      </div>

      <div className="space-y-1">
        <Label htmlFor="whatsapp-greeting">
          {translate("crm.whatsapp.settings.greeting")}
        </Label>
        <Textarea
          id="whatsapp-greeting"
          className="min-h-24 text-base"
          value={values.greeting}
          aria-invalid={invalid.includes("greeting")}
          onChange={(event) => set("greeting", event.target.value)}
        />
        <p className={HELP_CLASS}>
          {translate("crm.whatsapp.settings.greeting_help")}
        </p>
      </div>

      {invalid.length > 0 ? (
        <p role="alert" className="text-[15px] text-destructive">
          {translate("crm.whatsapp.settings.invalid", {
            fields: invalid
              .map((field) => translate(`crm.whatsapp.settings.field.${field}`))
              .join(", "),
          })}
        </p>
      ) : null}
      <Button
        type="button"
        className="h-11"
        disabled={isPending}
        onClick={onSave}
      >
        {translate("crm.whatsapp.settings.save")}
      </Button>
    </div>
  );
};

/**
 * Almikia Epic EW2 (TASK-080): the Direccion editor of the single `whatsapp_config` row. Window
 * durations, the free quota, the alert thresholds and the send rate limit are DATA (never
 * constants in code); the two go-live flags are shown clearly, OFF by default. The screen is
 * direccion-only (the page gate and the RLS of TASK-076).
 */
export const WhatsAppConfigCard = () => {
  const translate = useTranslate();
  const config = useGetOne<WhatsAppConfig>("whatsapp_config", { id: 1 });
  return (
    <SectionCard
      title={translate("crm.whatsapp.settings.config_title")}
      help={translate("crm.whatsapp.settings.config_help")}
    >
      {config.error ? (
        <ListErrorState
          message={translate("crm.whatsapp.settings.load_error")}
          onRetry={() => config.refetch()}
        />
      ) : !config.data ? (
        <ListCardsSkeleton rows={3} />
      ) : (
        <ConfigForm config={config.data} />
      )}
    </SectionCard>
  );
};
